import { Switch } from "@mui/material";
import { keyframes, margin, styled } from "@mui/system";

export const slideDown = keyframes({
  from: {
    opacity: 0,
    transform: "translateY(-8px) scale(0.98)",
  },
  to: {
    opacity: 1,
    transform: "translateY(0) scale(1)",
  },
});

export const Page = styled("div")(() => ({
  minHeight: "100vh",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  background: "linear-gradient(135deg, #f3e7ff 0%, #ffffff 50%, #e0f2ff 100%)",
  color: "#111827",
}));

export const Header = styled("header")(() => ({
  background: "#fff",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
}));

export const HeaderContainer = styled("div")(() => ({
  maxWidth: 1280,
  margin: "0 auto",
  padding: "16px 24px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

export const Logo = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginRight: "2rem",
}));

export const LogoIcon = styled("div")((logo) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  backgroundImage: `${logo}`,
}));

export const LoginButton = styled("button")(() => ({
  padding: "8px 24px",
  borderRadius: 8,
  border: "none",
  color: "#fff",
  fontWeight: 500,
  cursor: "pointer",
  background: "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)",
  transition: "all .3s",

  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(147,51,234,.4)",
  },
}));

export const Footer = styled("footer")(() => ({
  background: "#ffffff",
  borderTop: "1px solid #e5e7eb",
}));

export const FooterContainer = styled("div")(() => ({
  "& p": {
    margin: "auto",
  },
  maxWidth: 1280,
  margin: "0 auto",
  padding: "24px 24px",
  // color: "#6b7280",
  fontSize: 14,

  /* ===== QUILL ALIGN ===== */
  "& .ql-align-center": {
    textAlign: "center",
  },

  "& .ql-align-right": {
    textAlign: "right",
  },

  "& .ql-align-justify": {
    textAlign: "justify",
  },

  /* ===== QUILL IMAGE ===== */
  "& img": {
    maxWidth: "100%",
    height: "auto",
    //margin: "16px 0",
  },

  /* ===== QUILL HEADING (nếu có dùng h1,h3,h4) ===== */
  "& h1": {
    fontSize: 32,
    fontWeight: 800,
    margin: "32px 0 16px",
  },

  "& h3": {
    fontSize: 20,
    fontWeight: 600,
    margin: "24px 0 12px",
  },

  "& h4": {
    fontSize: 18,
    fontWeight: 600,
    margin: "20px 0 10px",
  },

  "& ol li[data-list='bullet']": {
    listStyleType: "disc",
  },

  "& ol li[data-list='ordered']": {
    listStyleType: "decimal",
  },

  /* ===== REMOVE EDITOR SELECTION OVERLAY ===== */
  "& .ql-cell-selected::after": {
    display: "none !important",
  },

  "& .ql-cell-selected-after::after": {
    display: "none !important",
  },

  "& .ql-cell-focused::after": {
    display: "none !important",
  },
}));

export const AuthBox = styled("div")(({ theme }) => ({
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: "2rem",
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

/* Google button container */
export const GoogleButton = styled("div")(() => ({
  cursor: "pointer",
}));

/* User menu */
export const UserMenuWrapper = styled("div")(() => ({
  position: "relative",
}));

export const UserTrigger = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
}));

export const DropdownMenu = styled("div")(() => ({
  position: "absolute",
  right: 0,
  top: "calc(100% + 8px)",
  minWidth: 180,

  backgroundColor: "#fff",
  borderRadius: 12,
  boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
  padding: "6px 0",

  animation: `${slideDown} 0.18s ease-out`,
  zIndex: 20,
}));

export const DropdownItem = styled("div")(() => ({
  padding: "10px 16px",
  fontSize: 14,
  cursor: "pointer",
  transition: "background-color 0.15s",
  color: "#000000",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
  display: "flex",
  gap: "5px",
  alignItems: "center",
}));

export const DropdownItemLogout = styled("div")(() => ({
  padding: "10px 16px",
  fontSize: 14,
  cursor: "pointer",
  color: "#e53935",
  fontWeight: 500,
  transition: "background-color 0.15s",
  display: "flex",
  gap: "5px",
  alignItems: "center",
  "&:hover": {
    backgroundColor: "#fee2e2",
  },
}));

export const UserName = styled("span")(() => ({
  marginLeft: 8,
  fontSize: 14,
  fontWeight: 500,
}));

export const Avatar = styled("img")(() => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
}));

export const NavMenu = styled("nav")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 24,

  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

export const NavItem = styled("div")(() => ({
  fontSize: 15,
  fontWeight: 500,
  color: "#374151",
  cursor: "pointer",
  position: "relative",
  padding: "6px 0",
  transition: "color .2s",

  "&:hover": {
    color: "#2563eb",
  },

  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    bottom: 0,
    width: "0%",
    height: 2,
    background: "linear-gradient(135deg, #9333ea, #2563eb)",
    transition: "width .25s ease",
    borderRadius: 2,
  },

  "&:hover::after": {
    width: "100%",
  },
}));

export const NavItemActive = styled(NavItem)(() => ({
  color: "#2563eb",

  "&::after": {
    width: "100%",
  },
}));

export const LanguageSwitch = styled(Switch)(({ theme }) => ({
  width: 50,
  height: 24,
  padding: 0,
  display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(27px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        background: "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)",
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 20,
    height: 20,
    backgroundColor: "#fff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 34 / 2,
    background: "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)",
    opacity: 1,
  },
}));

export const MobileMenuButton = styled("div")(({ theme }) => ({
  display: "none",
  fontSize: 24,
  cursor: "pointer",

  [theme.breakpoints.down("sm")]: {
    display: "block",
  },
}));

export const SidebarItemMain = styled("div")(() => ({
  padding: "0 20px",
}));

export const Sidebar = styled("div")<{ isClosing?: boolean }>(
  ({ isClosing }) => ({
    position: "fixed",
    top: 0,
    left: 0,
    width: 260,
    height: "100vh",
    background: "#fff",
    boxShadow: "2px 0 20px rgba(0,0,0,0.2)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    zIndex: 100,
    animation: `${isClosing ? slideOut : slideIn} .25s ease forwards`,
  }),
);

export const Overlay = styled("div")(() => ({
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.3)",
  zIndex: 99,
  transition: "all 0.3s ease",
}));

export const SidebarItem = styled("div")(() => ({
  padding: "16px 0",
  fontSize: 16,
  borderBottom: "1px solid #eee",
  cursor: "pointer",
}));

export const SidebarUserMobile = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: "1px solid #eee",
  padding: "16px 20px",
}));

export const LogoutIcon = styled("div")(() => ({ color: "#e53935" }));

const slideIn = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
`;
